import { RouteName } from '@mews-commander/enums';
import { useResolveRoute } from '@mews/routing';
import { lt, showSuccessAlert } from '@mews/framework';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { ExportDropdownButton as CommonExportDropdownButton, useReportIntervalLocal } from '../../../../common';
import { useDisplayOptions, useExportReservations, useScheduleExportReservations } from '../hooks';
import { ReservationsFilterValues } from '../schema';
import { useFilterParams, useOrderParams } from '../../common';
import { useReservationsTableStateContext } from '../../../../../ReservationsTableStateContext';
import { navigateInModal } from '../../../../../../app';
import { useLoaderContext } from '../../../../../LoaderProvider';

interface ExportDropdownButtonProps {
    filterValues: ReservationsFilterValues;
}

export const ExportDropdownButton = ({ filterValues }: ExportDropdownButtonProps) => {
    const { reservationsTableState: tableState } = useReservationsTableStateContext();
    const { toggleShowLoader } = useLoaderContext();
    const orderParams = useOrderParams(tableState?.sort);
    const filterParams = useFilterParams(tableState?.filters);

    const {
        startDate,
        endDate,
        productIds,
        serviceId,
        serviceOrderStatus,
        customerId,
        visibleColumns,
        filterProductsInInterval,
    } = filterValues;

    const displayOptions = useDisplayOptions(visibleColumns ?? [], filterProductsInInterval);

    const reportIntervalLocal = useReportIntervalLocal({ startDate, endDate });

    const { mutate: mutateExport } = useExportReservations({
        onSuccess: () => {
            showSuccessAlert({
                description: lt('ExportQueuedSuccessMessage'),
            });
        },
    });

    const handleExportClick = () => {
        if (!serviceId) return;

        mutateExport({
            guestInHouseFilters: {
                ...filterParams,
                filterProductsInInterval,
                serviceId,
                serviceOrderStatus,
                productIds,
                customerId: customerId ?? undefined,
                reportIntervalLocal,
            },
            ...orderParams,
            displayOptions,
        });
    };

    const resolveRoute = useResolveRoute();
    const dispatch = useCommanderThunkDispatch();

    const { mutateAsync: mutateScheduleExport } = useScheduleExportReservations({
        onSuccess: () => {
            showSuccessAlert({
                description: lt('GuestInHouseReportExportScheduleCreated'),
            });
        },
    });

    const handleCreateScheduleClick = async () => {
        if (!serviceId) return;

        toggleShowLoader();
        try {
            const { scheduleId } = await mutateScheduleExport({
                guestInHouseFilters: {
                    ...filterParams,
                    filterProductsInInterval,
                    serviceId,
                    serviceOrderStatus,
                    productIds,
                    customerId: customerId ?? undefined,
                },
                ...orderParams,
                displayOptions,
            });
            const url = resolveRoute(RouteName.GuestInHouseReportScheduleDetail, { query: { scheduleId } });
            dispatch(navigateInModal(url));
        } finally {
            toggleShowLoader();
        }
    };

    return (
        <CommonExportDropdownButton
            handleExportClick={handleExportClick}
            handleCreateScheduleClick={handleCreateScheduleClick}
        />
    );
};
